import React, { Fragment, useContext, useEffect } from 'react';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions, Menu, Transition } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';
import { HiChevronDown, HiMenu } from 'react-icons/hi';
import { UserContext } from "../context/UserContext";
import classnames from 'classnames';
import notFoundImage from '../assets/fox-not-found.webp';
import { getImageUrl } from '../lib/helpers';
import { getVenueBrandName } from '../lib/consts/venueTypes';

function VenueSelectTablet({ venues, selectedVenueId, onVenuePick }) {
  const selectedVenue = venues.find((v) => String(v.id) === String(selectedVenueId));
  const buttonLabel = selectedVenue?.name || 'Seleccionar tienda...';

  return (
    <div className="wa-venue-listbox relative w-full sm:w-auto min-w-0">
      <Listbox
        value={selectedVenueId ? String(selectedVenueId) : null}
        onChange={(venueId) => {
          if (!venueId) return;
          const venue = venues.find((v) => String(v.id) === String(venueId));
          if (venue && String(venue.id) !== String(selectedVenueId)) {
            onVenuePick(venue);
          }
        }}
      >
        <ListboxButton className="w-full sm:w-auto px-3 py-1 border border-gray-300 rounded-md text-sm text-left focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-between gap-2">
          <span className="whitespace-nowrap">{buttonLabel}</span>
          <HiChevronDown className="h-4 w-4 shrink-0 text-gray-500" aria-hidden />
        </ListboxButton>
        <ListboxOptions
          anchor="bottom start"
          className="z-50 mt-1 max-h-60 min-w-[var(--button-width)] overflow-auto rounded-md border border-gray-200 bg-white py-1 text-sm text-left shadow-lg focus:outline-none"
        >
          <div className="px-3 py-1.5 text-gray-400 select-none" aria-hidden>
            Seleccionar tienda...
          </div>
          {venues.map((venue) => (
            <ListboxOption
              key={venue.id}
              value={String(venue.id)}
              className={({ focus, selected }) =>
                classnames(
                  'cursor-pointer px-3 py-1.5',
                  focus && 'bg-blue-50',
                  selected && 'font-medium text-blue-700'
                )
              }
            >
              {venue.name}
            </ListboxOption>
          ))}
        </ListboxOptions>
      </Listbox>
    </div>
  );
}

export default function HeaderNew({ onMenuClick }) {
  const navigate = useNavigate();
  const [, setToken, ownedShops, venueID, venueName, venueImage, email, accessPrivileges, allVenues, changeVenue, , venueType] = useContext(UserContext);

  const handleLogout = () => {
    localStorage.removeItem('kongToken');
    setToken(null);
  };

  const isSuperuser = accessPrivileges === 'superadmin';
  const isStaff = accessPrivileges === 'staff';
  const venueList = isSuperuser ? allVenues : ownedShops;

  const selectedVenue = venueID && venueList
    ? venueList.find(v => {
      const vId = String(v.id);
      const selectedId = String(venueID);
      return vId === selectedId || vId.toLowerCase() === selectedId.toLowerCase();
    })
    : null;

  const shopName = selectedVenue?.name
    || venueName
    || (isSuperuser ? (allVenues?.[0]?.name || "No shop selected") : (ownedShops?.[0]?.name ?? "No shop found"));
  const appName = getVenueBrandName(venueType);

  useEffect(() => {
    document.title = shopName ? `${appName} · ${shopName}` : appName;
  }, [appName, shopName]);

  const venueImagePath = isStaff ? venueImage : (selectedVenue?.image_path || venueImage);
  const avatarUrl = getImageUrl(venueImagePath, notFoundImage);

  const pickVenue = (venue) => {
    if (!venue || !changeVenue) return;
    changeVenue(
      venue.id,
      venue.name,
      venue.image_path || '',
      venue.venue_type,
      venue.activation_status,
      venue.subscription_status
    );
  };

  const handleVenueChange = (e) => {
    const selectedVenueId = e.target.value;
    const venue = allVenues.find(v => String(v.id) === selectedVenueId);
    if (venue && changeVenue) {
      changeVenue(
        venue.id,
        venue.name,
        venue.image_path || '',
        venue.venue_type,
        venue.activation_status,
        venue.subscription_status
      );
    }
  };

  const roleLabel = (() => {
    const raw = (accessPrivileges || '').toString().toLowerCase();
    const norm = raw
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // strip accents
      .replace(/[^a-z0-9]+/g, '');
    if (norm.includes('superadmin') || norm.includes('superadministr')) return 'Super Admin';
    if (norm === 'owner' || norm === 'propietario') return 'Owner';
    if (norm === 'staff' || norm === 'personal') return 'Staff';
    return accessPrivileges;
  })();

  const nativeSelectClassName =
    'wa-venue-native-select hidden lg:block px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-auto';

  return (
    <header className="bg-white h-14 sm:h-16 max-lg:portrait:min-h-14 max-lg:portrait:h-auto max-lg:portrait:py-2 px-3 sm:px-4 flex items-center max-lg:portrait:items-start border-b border-gray-200 min-w-0 max-lg:portrait:gap-2">
      <button
        className="md:hidden p-2 mr-4 max-lg:portrait:-ml-1 max-lg:portrait:mr-0 max-lg:portrait:shrink-0 text-gray-600"
        onClick={onMenuClick}
        aria-label="Open sidebar"
      >
        <HiMenu size={24} />
      </button>

      <div className="wa-header-venuebar flex-1 min-w-0 text-left md:text-right flex flex-col sm:flex-row max-lg:portrait:flex-col items-start sm:items-center max-lg:portrait:items-start gap-2 sm:gap-4 max-lg:portrait:gap-1.5">
        {isSuperuser && allVenues && allVenues.length > 0 && (
          <>
            <select
              value={venueID || ''}
              onChange={handleVenueChange}
              className={nativeSelectClassName}
            >
              <option value="">Seleccionar tienda...</option>
              {allVenues.map((venue) => (
                <option key={venue.id} value={venue.id}>
                  {venue.name}
                </option>
              ))}
            </select>
            <VenueSelectTablet
              venues={allVenues}
              selectedVenueId={venueID}
              onVenuePick={pickVenue}
            />
          </>
        )}

        {!isSuperuser && ownedShops && ownedShops.length > 1 && (
          <>
            <select
              value={venueID || ''}
              onChange={(e) => {
                const selectedVenueId = e.target.value;
                const venue = ownedShops.find(v => String(v.id) === selectedVenueId);
                if (venue && changeVenue) {
                  changeVenue(
                    venue.id,
                    venue.name,
                    venue.image_path || '',
                    venue.venue_type,
                    venue.activation_status,
                    venue.subscription_status
                  );
                }
              }}
              className={nativeSelectClassName}
            >
              <option value="">Seleccionar tienda...</option>
              {ownedShops.map((venue) => (
                <option key={venue.id} value={venue.id}>
                  {venue.name}
                </option>
              ))}
            </select>
            <VenueSelectTablet
              venues={ownedShops}
              selectedVenueId={venueID}
              onVenuePick={pickVenue}
            />
          </>
        )}

        <h1 className="max-lg:portrait:hidden text-xs sm:text-sm font-bold truncate">
          <span className="hidden sm:inline">
            Estas en la tienda: {shopName}, usuario: {email ?? 'Sin email'}
            {accessPrivileges && (
              <span className="ml-1 text-purple-600 font-semibold">({roleLabel})</span>
            )}
          </span>
          <span className="inline sm:hidden">
            {shopName} | {email?.split('@')[0] ?? 'Sin email'}
            {accessPrivileges ? ` (${roleLabel})` : ''}
          </span>
        </h1>

        <div className="hidden max-lg:portrait:block min-w-0 text-left">
          <p className="text-xs sm:text-sm font-bold leading-snug text-[#0A0A0A] break-words">
            Estás en la tienda: {shopName}
          </p>
          <p className="mt-0.5 text-xs sm:text-sm leading-snug text-gray-700 break-all">
            Usuario: {email ?? 'Sin email'}
            {accessPrivileges ? (
              <span className="ml-1 font-semibold text-purple-600">({roleLabel})</span>
            ) : null}
          </p>
        </div>
      </div>

      <Menu as="div" className="relative max-lg:portrait:shrink-0">
        <Menu.Button className="ml-2 max-lg:portrait:ml-0 bg-gray-800 flex text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-neutral-400">
          <span className="sr-only">Open user menu</span>
          <img
            key={`avatar-${venueID}-${venueImagePath || 'default'}`}
            src={avatarUrl}
            alt={`${selectedVenue?.name || 'Venue'} avatar`}
            className="h-10 w-10 rounded-full object-cover"
            onError={(e) => {
              e.target.src = notFoundImage;
            }}
          />
        </Menu.Button>

        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="origin-top-right absolute right-0 mt-2 w-48 rounded-sm shadow-md p-1 bg-white ring-1 ring-black ring-opacity-5">
            <Menu.Item>
              {({ active }) => (
                <div
                  onClick={() => navigate('/profile')}
                  className={classnames(active && 'bg-gray-100', 'rounded-sm px-4 py-2 text-gray-700 cursor-pointer')}
                >
                  Your Profile
                </div>
              )}
            </Menu.Item>
            <Menu.Item>
              {({ active }) => (
                <div
                  onClick={() => navigate('/settings')}
                  className={classnames(active && 'bg-gray-100', 'rounded-sm px-4 py-2 text-gray-700 cursor-pointer')}
                >
                  Settings
                </div>
              )}
            </Menu.Item>
            <Menu.Item>
              {({ active }) => (
                <div
                  onClick={handleLogout}
                  className={classnames(active && 'bg-gray-100', 'rounded-sm px-4 py-2 text-gray-700 cursor-pointer')}
                >
                  Sign out
                </div>
              )}
            </Menu.Item>
          </Menu.Items>
        </Transition>
      </Menu>
    </header>
  );
}
